'use client';
import React, { useEffect, useRef, useState } from 'react';

const stats = [
  { id: 'bar-stat-1', value: 180, suffix: '+', label: 'Projects Delivered' },
  { id: 'bar-stat-2', value: 94, suffix: '', label: 'Happy Clients' },
  { id: 'bar-stat-3', value: 28, suffix: '', label: 'Countries Served' },
  { id: 'bar-stat-4', value: 42, suffix: '', label: 'Team Members' },
];

function Counter({ value, suffix, active }: { value: number; suffix: string; active: boolean }) {
  const [count, setCount] = useState(0);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active) return;
    const duration = 2000;
    const start = performance.now();
    const animate = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(eased * value));
      if (progress < 1) frameRef.current = requestAnimationFrame(animate);
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); };
  }, [active, value]);

  return <span data-component-id="src/app/homepage/components/StatsBar.tsx:29:9" data-component-path="src/app/homepage/components/StatsBar.tsx" data-component-line="29" data-component-end-line="29" data-component-file="StatsBar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22tab-nums%22%7D" className="tab-nums">{count}{suffix}</span>;
}

export default function StatsBar() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.4 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div data-component-id="src/app/homepage/components/StatsBar.tsx:46:4" data-component-path="src/app/homepage/components/StatsBar.tsx" data-component-line="46" data-component-end-line="75" data-component-file="StatsBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20py-16%20overflow-hidden%22%7D"
      ref={ref}
      className="relative py-16 overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 50%, #4c1d95 100%)',
      }}
    >
      {/* Noise overlay */}
      <div data-component-id="src/app/homepage/components/StatsBar.tsx:54:6" data-component-path="src/app/homepage/components/StatsBar.tsx" data-component-line="54" data-component-end-line="59" data-component-file="StatsBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20opacity-%5B0.04%5D%20pointer-events-none%22%7D"
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
      />

      <div data-component-id="src/app/homepage/components/StatsBar.tsx:61:6" data-component-path="src/app/homepage/components/StatsBar.tsx" data-component-line="61" data-component-end-line="74" data-component-file="StatsBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-screen-2xl%20mx-auto%20px-6%20lg%3Apx-10%20xl%3Apx-16%20relative%20z-10%22%7D" className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16 relative z-10">
        <div data-component-id="src/app/homepage/components/StatsBar.tsx:62:8" data-component-path="src/app/homepage/components/StatsBar.tsx" data-component-line="62" data-component-end-line="73" data-component-file="StatsBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-2%20lg%3Agrid-cols-4%20gap-8%22%7D" className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat) => (
            <div data-component-id="src/app/homepage/components/StatsBar.tsx:64:12" data-component-path="src/app/homepage/components/StatsBar.tsx" data-component-line="64" data-component-end-line="71" data-component-file="StatsBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%22%7D" key={stat.id} className="text-center">
              <div data-component-id="src/app/homepage/components/StatsBar.tsx:65:14" data-component-path="src/app/homepage/components/StatsBar.tsx" data-component-line="65" data-component-end-line="67" data-component-file="StatsBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22font-display%20font-800%20text-5xl%20xl%3Atext-6xl%20text-white%20mb-2%22%7D" className="font-display font-800 text-5xl xl:text-6xl text-white mb-2">
                <Counter data-component-id="src/app/homepage/components/StatsBar.tsx:66:16" data-component-path="src/app/homepage/components/StatsBar.tsx" data-component-line="66" data-component-end-line="66" data-component-file="StatsBar.tsx" data-component-name="Counter" data-component-content="%7B%22elementName%22%3A%22Counter%22%7D" value={stat.value} suffix={stat.suffix} active={visible} />
              </div>
              <div data-component-id="src/app/homepage/components/StatsBar.tsx:68:14" data-component-path="src/app/homepage/components/StatsBar.tsx" data-component-line="68" data-component-end-line="70" data-component-file="StatsBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-sm%20font-body%20font-500%20text-white%2F60%20tracking-wide%22%7D" className="text-sm font-body font-500 text-white/60 tracking-wide">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}